import Medusa from "@medusajs/js-sdk";
import { z } from "zod";
import { getCurrentUnlockToken } from "#/store/unlock";

const clientEnvSchema = z.object({
  MEDUSA_BACKEND_URL: z.string().url(),
  MEDUSA_PUBLISHABLE_API_KEY: z.string().min(1),
});

let cached: Medusa | undefined;
let cachedToken: string | null | undefined;

function buildMedusa(token: string | null): Medusa {
  const clientEnv = clientEnvSchema.parse({
    MEDUSA_BACKEND_URL: import.meta.env.MEDUSA_BACKEND_URL,
    MEDUSA_PUBLISHABLE_API_KEY: import.meta.env.MEDUSA_PUBLISHABLE_API_KEY,
  });
  const isBrowser = typeof window !== "undefined";
  const globalHeaders: Record<string, string> = {};
  if (token) {
    globalHeaders["x-unlock-token"] = token;
  }
  return new Medusa({
    baseUrl:
      isBrowser && !import.meta.env.DEV
        ? window.location.origin
        : clientEnv.MEDUSA_BACKEND_URL,
    publishableKey: clientEnv.MEDUSA_PUBLISHABLE_API_KEY,
    // auth: { type: "session" },
    auth: {type: "jwt"}, 
    globalHeaders,
  });
}

function getMedusa(): Medusa {
  const token = getCurrentUnlockToken();
  if (cached && cachedToken === token) return cached;
  cached = buildMedusa(token);
  cachedToken = token;
  return cached;
}

// Lazy facade so importing this module never triggers env validation.
// Tests reaching pure helpers transitively through this module no
// longer need MEDUSA_BACKEND_URL / MEDUSA_PUBLISHABLE_API_KEY set;
// only code that actually calls into the SDK does.
export const medusa = new Proxy({} as Medusa, {
  get(_target, prop, receiver) {
    const real = getMedusa();
    const value = Reflect.get(real, prop, receiver);
    return typeof value === "function" ? value.bind(real) : value;
  },
});

let adminClient: Medusa | undefined;

const adminEnvSchema = z.object({
  MEDUSA_BACKEND_URL: z.string().url(),
  MEDUSA_SECRET_API_KEY: z.string().min(1),
});

function getAdminMedusa(): Medusa {
  if (adminClient) return adminClient;
  const adminEnv = adminEnvSchema.parse({
    MEDUSA_BACKEND_URL: process.env.MEDUSA_BACKEND_URL,
    MEDUSA_SECRET_API_KEY: process.env.MEDUSA_SECRET_API_KEY,
  });
  adminClient = new Medusa({
    baseUrl: adminEnv.MEDUSA_BACKEND_URL,
    apiKey: adminEnv.MEDUSA_SECRET_API_KEY,
  });

  return adminClient;
}

export const medusaAdmin = new Proxy({} as Medusa, {
  get(_target, prop, receiver) {
    const real = getAdminMedusa();
    const value = Reflect.get(real, prop, receiver);
    return typeof value === "function" ? value.bind(real) : value;
  },
});